import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, EyeOff, FileUp, Link2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/components/rbac/use-permission';
import { P } from '@/lib/rbac/permissions.generated';
import {
  billingApi,
  type BankEvent,
  type BankEventStatus,
  type BankStatementImport,
  type ContractAccount,
} from '@/lib/api/billing';
import { BANK_EVENT_STATUS, formatCents, formatIsoDate } from '@/lib/billing';

const FINANZEN = P.buchhaltung.finanzdaten_und_zahlungen;
const OPEN: BankEventStatus = 'unmatched';

type AccountHit = ContractAccount & { debtorName?: string | null; debtorNumber?: string | null };

export default function BankStatementsPage() {
  const canCapture = usePermission(FINANZEN.zahlung_erfassen);
  const [imports, setImports] = useState<BankStatementImport[]>([]);
  const [events, setEvents] = useState<BankEvent[]>([]);
  const [status, setStatus] = useState<BankEventStatus | 'all'>(OPEN);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState<BankEvent | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [importList, eventList] = await Promise.all([
        billingApi.listBankStatements(),
        billingApi.listBankEvents({ status }),
      ]);
      setImports(Array.isArray(importList?.items) ? importList.items : []);
      setEvents(Array.isArray(eventList?.items) ? eventList.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kontoauszüge konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await billingApi.uploadBankStatement(file);
      if (result.alreadyImported) {
        toast.info(`${result.fileName} wurde bereits importiert`);
      } else {
        toast.success(
          `${result.entryCount} Umsätze gelesen: ${result.matchedCount} zugeordnet, ${result.unmatchedCount} offen` +
            (result.duplicateCount ? `, ${result.duplicateCount} schon bekannt` : ''),
        );
      }
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Import fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const ignore = async (event: BankEvent) => {
    try {
      await billingApi.ignoreBankEvent(event.id);
      toast.success('Umsatz als nicht relevant markiert');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Aktion fehlgeschlagen');
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">Kontoauszüge</h1>
          <p className="text-muted-foreground">
            camt.053-Dateien aus dem Onlinebanking importieren. Lastschriften, Rücklastschriften und Überweisungen
            mit Vertragskontonummer werden automatisch verbucht.
          </p>
        </div>
        {canCapture && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept=".xml,application/xml,text/xml"
              className="hidden"
              onChange={(event) => void upload(event.target.files?.[0])}
            />
            <Button onClick={() => fileInput.current?.click()} disabled={uploading}>
              <FileUp className="mr-2 h-4 w-4" />
              {uploading ? 'Importiert …' : 'Kontoauszug importieren'}
            </Button>
          </>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>Umsätze</CardTitle>
            <CardDescription>
              Nicht zugeordnete Gutschriften einem Vertragskonto zuweisen oder als nicht relevant markieren.
            </CardDescription>
          </div>
          <Select value={status} onValueChange={(value) => setStatus(value as BankEventStatus | 'all')}>
            <SelectTrigger className="w-52" aria-label="Status filtern">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Umsätze</SelectItem>
              {Object.entries(BANK_EVENT_STATUS).map(([value, entry]) => (
                <SelectItem key={value} value={value}>
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingState message="Umsätze werden geladen …" />
          ) : events.length === 0 ? (
            <EmptyState message={status === OPEN ? 'Alles zugeordnet.' : 'Keine Umsätze.'} />
          ) : (
            <div className="overflow-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Buchung</TableHead>
                    <TableHead>Gegenpartei</TableHead>
                    <TableHead>Verwendungszweck</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => {
                    const open = event.matchStatus === 'unmatched' || event.matchStatus === 'needs_review';
                    return (
                      <TableRow key={event.id}>
                        <TableCell className="tabular-nums">{formatIsoDate(event.bookingDate ?? event.valueDate)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{event.counterpartyName ?? '–'}</div>
                          {event.counterpartyIban && (
                            <div className="text-xs text-muted-foreground">{event.counterpartyIban}</div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-sm text-sm">
                          <div className="truncate" title={event.remittance ?? ''}>
                            {event.remittance || '–'}
                          </div>
                          {(event.endToEndId || event.returnReason || event.note) && (
                            <div className="truncate text-xs text-muted-foreground">
                              {[event.endToEndId, event.returnReason && `Rückgabe ${event.returnReason}`, event.note]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${(event.amountCents ?? 0) < 0 ? 'text-destructive' : ''}`}
                        >
                          {formatCents(event.amountCents)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={BANK_EVENT_STATUS[event.matchStatus]?.variant ?? 'outline'} size="sm">
                            {BANK_EVENT_STATUS[event.matchStatus]?.label ?? event.matchStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {canCapture && open && (
                            <div className="flex justify-end gap-2">
                              {(event.amountCents ?? 0) > 0 && (
                                <Button variant="outline" size="sm" onClick={() => setAssigning(event)}>
                                  <Link2 className="mr-2 h-4 w-4" />
                                  Zuordnen
                                </Button>
                              )}
                              <Button variant="outline" size="sm" onClick={() => void ignore(event)}>
                                <EyeOff className="mr-2 h-4 w-4" />
                                Ignorieren
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Importierte Dateien</CardTitle>
          <CardDescription>Jede Datei wird genau einmal verarbeitet und unverändert aufbewahrt.</CardDescription>
        </CardHeader>
        <CardContent>
          {imports.length === 0 ? (
            <EmptyState message="Noch kein Kontoauszug importiert." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datei</TableHead>
                  <TableHead>Importiert</TableHead>
                  <TableHead className="text-right">Umsätze</TableHead>
                  <TableHead className="text-right">Zugeordnet</TableHead>
                  <TableHead className="text-right">Offen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {imports.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell className="font-medium">{record.fileName}</TableCell>
                    <TableCell className="tabular-nums">{formatIsoDate(record.createdAt)}</TableCell>
                    <TableCell className="text-right tabular-nums">{record.entryCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{record.matchedCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{record.unmatchedCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {assigning && (
        <AssignDialog
          event={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={async () => {
            setAssigning(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

interface AssignDialogProps {
  event: BankEvent;
  onClose: () => void;
  onAssigned: () => Promise<void>;
}

function AssignDialog({ event, onClose, onAssigned }: AssignDialogProps) {
  const [term, setTerm] = useState(event.counterpartyName ?? '');
  const [hits, setHits] = useState<AccountHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const search = useCallback(async (value: string) => {
    if (value.trim().length < 2) {
      setHits([]);
      return;
    }
    setSearching(true);
    try {
      const response = await billingApi.listAccounts({ q: value.trim(), billingEntityId: event.billingEntityId ?? undefined });
      setHits(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Suche fehlgeschlagen');
    } finally {
      setSearching(false);
    }
  }, [event.billingEntityId]);

  useEffect(() => {
    const timer = window.setTimeout(() => void search(term), 300);
    return () => window.clearTimeout(timer);
  }, [term, search]);

  const assign = async (account: AccountHit) => {
    setSaving(true);
    try {
      await billingApi.assignBankEvent(event.id, account.id);
      toast.success(`${formatCents(event.amountCents)} auf ${account.accountNumber} gebucht`);
      await onAssigned();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Zuordnung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gutschrift zuordnen</DialogTitle>
          <DialogDescription>
            {formatCents(event.amountCents)} von {event.counterpartyName ?? 'unbekannt'} am{' '}
            {formatIsoDate(event.bookingDate)} – „{event.remittance || 'ohne Verwendungszweck'}“. Die Zahlung gleicht
            die ältesten offenen Posten des Vertragskontos aus; ein Rest bleibt als Guthaben.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="assign-search">Vertragskonto, Debitorennummer oder Name</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="assign-search"
              className="pl-9"
              value={term}
              onChange={(changeEvent) => setTerm(changeEvent.target.value)}
              placeholder="z. B. VK-000123"
            />
          </div>
        </div>
        <div className="max-h-[40vh] overflow-auto rounded-lg border">
          {searching ? (
            <LoadingState message="Sucht …" />
          ) : hits.length === 0 ? (
            <EmptyState message={term.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben.' : 'Kein Vertragskonto gefunden.'} />
          ) : (
            <Table>
              <TableBody>
                {hits.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium tabular-nums">{account.accountNumber}</TableCell>
                    <TableCell>
                      {account.debtorName ?? '–'}
                      <div className="text-xs text-muted-foreground">Debitor {account.debtorNumber ?? '–'}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">Saldo {formatCents(account.balanceCents ?? 0)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" disabled={saving} onClick={() => void assign(account)}>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Buchen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
